// Permission tabs (catalog v2, MBC-84 Phase 2). Order = display order in the preview matrix.
export enum EPermissionTab {
  Users = 'users',
  Stores = 'stores',
  Menu = 'menu',
  Promos = 'promos',
  Feedback = 'feedback',
  Statistics = 'statistics',
  QR = 'qr',
  Bitecoins = 'bitecoins',
  Portal = 'portal',
  Announcements = 'announcements',
  Customers = 'customers',
  RegionsGroups = 'regions_groups',
  // CIR modules — launch scope.
  CirTrackStore = 'cir_track_store',
  CirMonitorSales = 'cir_monitor_sales',
  CirDeliveryHistory = 'cir_delivery_history',
  // CIR modules — excluded at launch (reserved; granted to no profile in v1).
  CirAnalyzeChannels = 'cir_analyze_channels',
  CirImproveQsc = 'cir_improve_qsc',
  CirDevelopCrew = 'cir_develop_crew',
  CirOrderHistory = 'cir_order_history',
  CirSalesReports = 'cir_sales_reports',
  CirStockCashRecon = 'cir_stock_cash_recon',
  CirGeneratePO = 'cir_generate_po',
  CirTrackPO = 'cir_track_po',
}

// CIR-portal module tabs — drive the "CIR Portal" header item + the CIR sidebar for
// franchisor/superadmin users. Order = sidebar display order.
export const CIR_PERMISSION_TABS: EPermissionTab[] = [
  EPermissionTab.CirTrackStore,
  EPermissionTab.CirMonitorSales,
  EPermissionTab.CirDeliveryHistory,
  EPermissionTab.CirAnalyzeChannels,
  EPermissionTab.CirImproveQsc,
  EPermissionTab.CirDevelopCrew,
  EPermissionTab.CirOrderHistory,
  EPermissionTab.CirSalesReports,
  EPermissionTab.CirStockCashRecon,
  EPermissionTab.CirGeneratePO,
  EPermissionTab.CirTrackPO,
];

// Access levels are ordinal: granted >= required.
export enum EAccessLevel {
  NoAccess = 0,
  ViewOnly = 1,
  Edit = 2,
  Download = 3,
}

export const permissionTabLabel: Record<EPermissionTab, string> = {
  [EPermissionTab.Users]: 'Users',
  [EPermissionTab.Stores]: 'Stores',
  [EPermissionTab.Menu]: 'Menu',
  [EPermissionTab.Promos]: 'Promos',
  [EPermissionTab.Feedback]: 'Feedback',
  [EPermissionTab.Statistics]: 'Statistics',
  [EPermissionTab.QR]: 'QR',
  [EPermissionTab.Bitecoins]: 'Bitecoins',
  [EPermissionTab.Portal]: 'Portal (Revenue)',
  [EPermissionTab.Announcements]: 'Announcements',
  [EPermissionTab.Customers]: 'Customers',
  [EPermissionTab.RegionsGroups]: 'Regions & Groups',
  [EPermissionTab.CirTrackStore]: 'CIR: Track Store Real-Time',
  [EPermissionTab.CirMonitorSales]: 'CIR: Monitor Sales',
  [EPermissionTab.CirDeliveryHistory]: 'CIR: View Delivery History',
  [EPermissionTab.CirAnalyzeChannels]: 'CIR: Analyze Channels',
  [EPermissionTab.CirImproveQsc]: 'CIR: Improve QSC',
  [EPermissionTab.CirDevelopCrew]: 'CIR: Develop Crew',
  [EPermissionTab.CirOrderHistory]: 'CIR: Order History',
  [EPermissionTab.CirSalesReports]: 'CIR: Sales Reports',
  [EPermissionTab.CirStockCashRecon]: 'CIR: Stock/Cash Reconciliation',
  [EPermissionTab.CirGeneratePO]: 'CIR: Generate PO',
  [EPermissionTab.CirTrackPO]: 'CIR: Track PO Real-Time',
};

export const accessLevelLabel: Record<EAccessLevel, string> = {
  [EAccessLevel.NoAccess]: 'No Access',
  [EAccessLevel.ViewOnly]: 'View Only',
  [EAccessLevel.Edit]: 'Edit',
  [EAccessLevel.Download]: 'Download',
};

// tab -> level. A profile omits NoAccess tabs; treat a missing tab as NoAccess.
export type IPermissionMatrix = Components.Schemas.ProfileDto['matrix'];

// MBC-84 Phase 2: `id` widened locally to string — the generated union still only knows the v1
// profile ids. Remove the Omit-override after `yarn models:build`.
export interface IProfile extends Omit<Components.Schemas.ProfileDto, 'id'> {
  id: string;
}

export interface IProfileCollection extends Components.Schemas.ProfileCollectionDto {}

// Store scope assigned to a franchisor user.
export enum EStoreScopeType {
  All = 'all',
  Region = 'region',
  Group = 'group',
  Stores = 'stores',
}

export const storeScopeTypeLabel: Record<EStoreScopeType, string> = {
  [EStoreScopeType.All]: 'All stores',
  [EStoreScopeType.Region]: 'Region',
  [EStoreScopeType.Group]: 'Group',
  [EStoreScopeType.Stores]: 'Specific stores',
};

export interface IStoreScope {
  type: EStoreScopeType | null;
  ids: string[];
}
